import type { SearchTab } from '../../search/search-contract';

/** Which way {@link cycleSearchTab} walks the strip. */
export type TabStep = 1 | -1;

/**
 * Value equality for a {@link SearchTab}.
 *
 * Not identity: the strip is rebuilt whenever the registered apps change, so the
 * active tab held in state may be a different object describing the same tab.
 */
export function sameSearchTab(a: SearchTab, b: SearchTab): boolean {
  if (a.kind === 'all' || b.kind === 'all') {
    return a.kind === b.kind;
  }
  return a.moduleId === b.moduleId;
}

/**
 * The tab `Tab` / `Shift+Tab` moves to, wrapping at both ends (需求 FR-14 §5.2).
 *
 * Wrapping rather than stopping at the edges: `Tab` is the only key that can
 * walk this strip — ←→ move the caret inside the input and ↑↓ belong to the
 * result list — so stopping at the last tab would make `Shift+Tab` the only way
 * back, which is exactly what wrapping saves the user from needing.
 *
 * Returns the element from `order`, not a fresh object: `useSearch` keys its
 * parsed query off the active tab's identity, so handing back a new object that
 * merely looks the same would re-run every provider.
 */
export function cycleSearchTab(
  order: readonly SearchTab[],
  current: SearchTab,
  step: TabStep,
): SearchTab {
  if (order.length < 2) {
    return current;
  }
  const at = order.findIndex((tab) => sameSearchTab(tab, current));
  // The active tab is no longer in the strip — its app unregistered while the
  // box was open. Step from the head so the key still lands somewhere.
  const from = at === -1 ? 0 : at;
  return order[(from + step + order.length) % order.length];
}
